import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  TopStrip, Nav, FooterFull, CtaBand, Slash, WordMark,
  ProposalModal, RopaModal,
  useOpenModalFromQuery,
  type DiagAnswer, type PackageName, type ModalKind, type NavActive,
} from './landing-shared';
import { submitWaitlist } from '@/lib/leads';
import './landing.css';

const FAQS = [
  { q: 'What exactly does a DPO do?', a: "A Data Protection Officer monitors your organisation's compliance with GDPR and other data protection laws, advises on data processing activities, conducts internal audits, manages Data Protection Impact Assessments (DPIAs), and acts as the primary contact for data subjects and the supervisory authority (e.g. the ICO in the UK or the relevant EU national authority)." },
  { q: "Is WeDPO's DPO legally recognised as our official DPO?", a: 'Yes. Under GDPR Article 37(6), a DPO can be an external service provider. We provide a formal designation letter and register the DPO with the relevant supervisory authority on your behalf. Our DPOs have the qualifications, independence, and resources required under Articles 37 to 39.' },
  { q: 'Can one DPO cover multiple companies in a group?', a: 'Yes - GDPR explicitly allows a single DPO to serve a group of undertakings or several public authorities. WeDPO manages multi-entity engagements regularly. We assess the structure and complexity to ensure the DPO (or team, where needed) can perform their tasks effectively across all entities.' },
  { q: 'What is the difference between Basic and Advanced?', a: 'Basic covers the mandatory DPO appointment, DSR response, initial audit and compliance scanner access with capped monthly consultation. Advanced adds hands-on ROPA management and tool, weekly consultation hours, full template access, staff training, vendor toolkit, and awareness Q&A sessions - suited to companies with active compliance programmes and growing vendor ecosystems.' },
  { q: 'What does the compliance scanner tool do?', a: 'The WeDPO compliance scanner identifies gaps in your data protection practices. It reviews your privacy notices, cookie setup, and records of processing to flag areas needing attention - giving you a live readiness score and a prioritised action list. Available on both Basic and Advanced packages.' },
  { q: 'Is there a minimum contract term?', a: 'Monthly plans have a 3-month minimum to allow proper onboarding and DPO registration. Annual plans (10% discount) are billed upfront or in quarterly instalments. Custom engagements are negotiated per proposal and typically carry a 6 to 12 month term.' },
  { q: 'Which supervisory authorities do you cover?', a: 'We cover EU/EEA supervisory authorities under GDPR, the ICO under UK GDPR, and the Israeli Privacy Protection Authority (PPA) under Israeli privacy law. For other jurisdictions (CCPA, LGPD, PDPA, etc.), we work with local counsel - discuss your needs in a proposal call.' },
  { q: 'What happens if we receive a regulator inquiry?', a: 'For Basic and Advanced clients, regulator response support is available as an out-of-scope add-on billed at an hourly rate. Custom package clients receive full audit and regulator response support as part of their engagement. In all cases, your WeDPO DPO is the named contact for the supervisory authority.' },
  { q: 'How quickly can we get started?', a: 'Basic and Advanced onboarding typically takes 5 to 7 business days from contract signature to DPO designation and supervisory authority registration. We send an access checklist and handle all the filings. Custom engagements begin with a scoping call.' },
  { q: 'Does WeDPO also serve as our EU or UK representative?', a: "The DPO role is distinct from the EU/UK representative role under GDPR Article 27. We can advise on whether you need a representative and connect you with an appropriate provider. If you are based outside the EU/UK and process EU or UK personal data, you may need both - mention this in your proposal call and we'll include it." },
];

const PILLARS = [
  { num: '01', tag: 'Knowledge layer', title: 'Expert knowledge, structured', text: '15 years of real-world DPO expertise, regulatory guidance and jurisdiction-specific knowledge, organised and instantly accessible. Get accurate answers grounded in professional practice, not generic internet results.' },
  { num: '02', tag: 'Operations', title: 'Everything in one place', text: 'ROPA management, GDPR audits, policy libraries, DPIA workflows and incident tracking, all in a single dashboard. No more spreadsheets, shared drives or chasing emails. Your compliance, always up to date.' },
  { num: '03', tag: 'AI-powered', title: 'AI privacy assistant', text: 'Ask any privacy question and get an expert-grounded answer in seconds. Our AI is trained on real DPO practice, not the open internet, so you get reliable, actionable guidance specific to your compliance context.' },
];

const SERVICE_FEATURES = [
  'DPO designation',
  'Expert DPO support & consultation',
  'Cross-domain expertise',
  'ROPA management tool',
  'Unique compliance scanner',
  'Templates & toolkits',
];

const PLATFORM_FEATURES = [
  'Compliance readiness & audits',
  'Automated ROPA Manager',
  'Policy Center',
  'Impact Assessment',
  'Incidents management',
  'Automated vendor management',
  'Gen AI assistant',
  'Agentic AI workflows',
];

type DiagState = { q1?: DiagAnswer; q2?: DiagAnswer; q3?: DiagAnswer };

const NAV_TO_ACTIVE: Record<string, NavActive> = {
  why: 'home-why',
  platform: 'home-platform',
  faq: 'home-faq',
  contact: 'home-contact',
};

export function LandingPage() {
  const location = useLocation();

  // Diagnostic
  const [dStep, setDStep] = useState(1);
  const [dAns, setDAns] = useState<DiagState>({});
  const [dResult, setDResult] = useState(false);

  // FAQ
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  // Modals
  const [modal, setModal] = useState<ModalKind>(null);
  const [selectedPackage, setSelectedPackage] = useState<PackageName | ''>('');

  // Active nav section
  const [activeSection, setActiveSection] = useState<keyof typeof NAV_TO_ACTIVE | null>(null);

  // Open modal from ?open=proposal&pkg=Advanced query (from packages page CTA, etc.)
  useOpenModalFromQuery(setModal, setSelectedPackage);

  // Reveal-on-scroll
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      }),
      { threshold: 0.1 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Scroll-spy for nav active state
  useEffect(() => {
    const ids = Object.keys(NAV_TO_ACTIVE);
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id as keyof typeof NAV_TO_ACTIVE);
          }
        });
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: 0 },
    );
    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  // Smooth scroll to hash on mount / hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        // Wait a tick so layout is in place.
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    }
  }, [location.hash]);

  const openProposal = (pkg?: PackageName) => {
    setSelectedPackage(pkg ?? '');
    setModal('proposal');
  };

  const resetDiag = () => { setDStep(1); setDAns({}); setDResult(false); };

  // ─── Diagnostic logic - identical to the original implementation ───
  const diagResult = () => {
    const q1 = dAns.q1 ?? 'no';
    const q2 = dAns.q2 ?? 'no';
    const q3 = dAns.q3 ?? 'no';
    const mandatory = q1 === 'yes' || q2 === 'yes' || q3 === 'yes';
    const borderline = !mandatory && (q1 === 'unsure' || q2 === 'unsure' || q3 === 'unsure');
    if (mandatory) {
      let text: string, ico: string;
      if (q1 === 'yes') {
        text = 'As a public authority or body, you have an unconditional legal obligation to appoint a DPO under GDPR, regardless of the nature or scale of your processing activities.';
        ico = 'Article 37(1)(a) GDPR: all public authorities and bodies must appoint a DPO, except courts acting in their judicial capacity.';
      } else if (q2 === 'yes') {
        text = 'Your core activities require regular and systematic monitoring of individuals on a large scale, one of the three conditions that trigger a mandatory DPO under GDPR.';
        ico = 'Article 37(1)(b) GDPR: organisations whose core activities involve large-scale systematic monitoring, such as tracking, profiling or CCTV at scale, must appoint a DPO.';
      } else {
        text = 'Your organisation processes special category or criminal convictions data at scale as a core activity, one of the three conditions that trigger a mandatory DPO under GDPR.';
        ico = 'Article 37(1)(c) GDPR: organisations that process health, biometric, genetic or other special category data on a large scale as a core activity must appoint a DPO.';
      }
      return {
        badgeClass: 'badge-mandatory',
        badgeText: '⚠ DPO appointment is legally required',
        title: 'You are required to appoint a DPO',
        text,
        ico,
        pkg: 'Recommended: Advanced or Custom package',
        suggestedPackage: 'Advanced' as PackageName,
      };
    }
    if (borderline) {
      return {
        badgeClass: 'badge-recommended',
        badgeText: '◎ DPO is strongly recommended',
        title: 'Your situation requires further assessment',
        text: 'One or more criteria are uncertain. Where it is not obvious that no DPO is required, organisations should document their reasoning and strongly consider appointing one to eliminate regulatory exposure.',
        ico: 'Under GDPR, any decision not to appoint a DPO where the position is unclear should be documented with reasons. A voluntary DPO appointment still requires registration with the relevant supervisory authority.',
        pkg: 'Recommended: Basic or Advanced package',
        suggestedPackage: 'Basic' as PackageName,
      };
    }
    return {
      badgeClass: 'badge-voluntary',
      badgeText: '✓ No mandatory DPO requirement identified',
      title: 'A DPO is not legally required, but recommended',
      text: 'Based on the three GDPR criteria, your organisation does not appear to meet the threshold for a mandatory DPO. However, all organisations should have someone responsible for data protection, and appointing a voluntary DPO demonstrates best practice.',
      ico: 'Even where a DPO is not mandatory, GDPR requires organisations to have sufficient staff and resources to meet their data protection obligations. A voluntary DPO can be registered with the relevant supervisory authority.',
      pkg: 'Suggested: Basic package or a consultation call',
      suggestedPackage: 'Basic' as PackageName,
    };
  };

  const diagQuestions = [
    {
      num: 'Question 1 of 3',
      question: 'Is your organisation a public authority or public body?',
      hint: 'This includes government departments, local authorities, NHS bodies, schools, universities, and other bodies carrying out public functions as defined under Section 7 of the Data Protection Act 2018. Courts acting in a judicial capacity are exempt.',
      key: 'q1' as const,
      options: [
        ['yes', 'Yes, we are a public authority or public body'],
        ['no', 'No, we are a private or third-sector organisation'],
        ['unsure', 'Not sure, our legal status is unclear'],
      ] as [DiagAnswer, string][],
    },
    {
      num: 'Question 2 of 3',
      question: "Do your organisation's core activities require regular and systematic monitoring of individuals on a large scale?",
      hint: 'Examples: behavioural advertising, online tracking and profiling, CCTV at scale, location tracking, loyalty programme analytics, insurance or credit scoring. "Core activities" means your primary business objectives, not support functions like HR or payroll processing.',
      key: 'q2' as const,
      options: [
        ['yes', 'Yes, systematic monitoring of individuals is central to our business'],
        ['no', 'No, this is not a core activity for us'],
        ['unsure', "Possibly, I'm uncertain whether our scale qualifies"],
      ] as [DiagAnswer, string][],
    },
    {
      num: 'Question 3 of 3',
      question: "Do your organisation's core activities involve processing, on a large scale, special category data or data relating to criminal convictions and offences?",
      hint: 'Special category data includes: health and medical records, biometric and genetic data, racial or ethnic origin, political opinions, religious beliefs, trade union membership, and data about sex life or sexual orientation. This applies when such processing is a core part of your business e.g. a hospital, insurer, or healthtech platform not incidental HR processing.',
      key: 'q3' as const,
      options: [
        ['yes', 'Yes, we process special category or criminal convictions data at scale as a core activity'],
        ['no', 'No, we do not process this type of data at scale'],
        ['unsure', "Possibly, I process some sensitive data but I'm unsure whether our scale qualifies"],
      ] as [DiagAnswer, string][],
    },
  ];

  const current = diagQuestions[dStep - 1];
  const result = dResult ? diagResult() : null;

  const handleNextOrResult = () => {
    if (!dAns[`q${dStep}` as keyof DiagState]) {
      alert('Please select an answer before continuing.');
      return;
    }
    if (dStep < 3) setDStep((s) => s + 1);
    else setDResult(true);
  };

  const navActive: NavActive = activeSection ? (NAV_TO_ACTIVE[activeSection] ?? null) : null;

  return (
    <div className="wedpo-landing">
      <TopStrip />
      <Nav active={navActive} onProposal={() => openProposal()} />

      {/* HERO */}
      <section className="hero">
        <div className="hero-grid-bg" />
        <svg className="hero-echo" viewBox="0 0 360 800" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="hero-echo-grad" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7B3FE4" stopOpacity="0.18" />
              <stop offset="50%" stopColor="#3E5CCF" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#3EC7D9" stopOpacity="0.20" />
            </linearGradient>
          </defs>
          <line x1="60" y1="750" x2="320" y2="40" stroke="url(#hero-echo-grad)" strokeWidth="44" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>

        <div className="hero-content">
          <WordMark large />
          <div className="hero-tagline-primary"><em>DPO</em> as a Service.</div>
          <p className="hero-tagline-sub">
            And an AI-driven OS, knowledge and management system<br />
            for privacy and data protection.
          </p>

          <div className="hero-actions">
            <div className="cta-stack">
              <Link to="/packages#choose-your-package" className="btn-primary">DPO as a Service Packages →</Link>
              <a href="#diagnostic" className="hero-sub-cta">Do I need a DPO? →</a>
            </div>
            <button
              type="button"
              onClick={() => openProposal()}
              className="btn-outline"
            >
              Schedule a full system demo
            </button>
          </div>

          <div className="hero-trust">
            <div className="trust-bar">
              GDPR <span className="pipe">|</span>
              Global Privacy and AI Compliance <span className="pipe">|</span>
              Compliance Enforcement Tracker <span className="pipe">|</span>
              Compliance Automation Tools
            </div>
          </div>
        </div>

        <div className="cred" aria-label="Practice highlights">
          <div className="cred-row">
            <div className="cred-cell">
              <span className="num">20+</span>
              <span className="lbl">Jurisdictions covered</span>
            </div>
            <div className="cred-cell">
              <span className="num">15+ yrs</span>
              <span className="lbl">Of expertise</span>
            </div>
            <div className="cred-cell">
              <span className="num">10+</span>
              <span className="lbl">Automation modules</span>
            </div>
            <div className="cred-cell">
              <span className="num">6+</span>
              <span className="lbl">Languages supported (DPO services)</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section why-section" id="why">
        <div className="section-inner">
          <div className="section-header reveal">
            <div className="section-label">Why WeDPO</div>
            <h2 className="section-title">A decade of expertise. Now <em>AI-accelerated</em>.</h2>
            <div className="why-body">
              <p>Your compliance programme, <strong>fully managed</strong>. From DPO designation to ROPA automation, compliance audits, regulatory updates and more, we handle the complexity so you don't have to.</p>
              <p>Our DPO-as-a-Service and "WeDPO program management system" include the following tools and services (some available as standalone).</p>
            </div>
          </div>

          <div className="split-cards">
            <article className="split-card split-card--light reveal reveal-delay-1">
              <div className="split-head">
                <span className="split-eyebrow">The Service</span>
              </div>
              <h3 className="split-title">DPO as a Service</h3>
              <p className="split-blurb">A named Data Protection Officer on retainer, with the operational toolkit to actually do the job.</p>
              <ol className="feat-list">
                {SERVICE_FEATURES.map((f) => <li key={f}>{f}</li>)}
              </ol>
              <Link to="/packages#choose-your-package" className="split-cta split-cta--ink">See offers →</Link>
            </article>

            <article className="split-card split-card--dark reveal reveal-delay-2">
              <div className="split-head">
                <span className="split-eyebrow">The Platform</span>
                <span className="split-soon">Coming Soon</span>
              </div>
              <h3 className="split-title">WeDPO Full Platform</h3>
              <p className="split-blurb">The operating system for your compliance programme. Expert knowledge, tools and AI in one place.</p>
              <ol className="feat-list">
                {PLATFORM_FEATURES.map((f) => <li key={f}>{f}</li>)}
                <li className="more-row">and more, rolling out with the platform</li>
              </ol>
              <a href="#waitlist" className="split-cta split-cta--paper">Join the waitlist →</a>
            </article>
          </div>
        </div>
      </section>

      {/* DIAGNOSTIC */}
      <section className="section" id="diagnostic">
        <div className="section-inner">
          <div className="section-header reveal" style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 3rem' }}>
            <div className="section-label">DPO Assessment</div>
            <h2 className="section-title">Do I need a DPO?</h2>
          </div>
          <div className="diag-card reveal">
            {!dResult && current && (
              <div className="diag-step active">
                <div className="diag-q-num">{current.num}</div>
                <div className="diag-question">{current.question}</div>
                <div className="diag-hint">{current.hint}</div>
                <div className="diag-options">
                  {current.options.map(([val, label]) => {
                    const selected = dAns[current.key] === val;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setDAns((a) => ({ ...a, [current.key]: val }))}
                        className={`diag-opt${selected ? ' selected' : ''}`}
                      >
                        <div className="diag-opt-radio" />
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="diag-nav">
                  <div className="diag-progress">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={`diag-dot${i <= dStep ? ' done' : ''}`} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {dStep > 1 && (
                      <button type="button" onClick={() => setDStep((s) => s - 1)} className="diag-btn diag-btn-back">← Back</button>
                    )}
                    <button type="button" onClick={handleNextOrResult} className="diag-btn diag-btn-next">
                      {dStep < 3 ? 'Next →' : 'See my result →'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {result && (
              <div className="diag-step diag-result active">
                <div>
                  <div className={`diag-result-badge ${result.badgeClass}`}>{result.badgeText}</div>
                </div>
                <div className="diag-result-title">{result.title}</div>
                <p className="diag-result-text">{result.text}</p>
                <div className="diag-ico-note">
                  <strong style={{ color: 'rgba(244,244,248,0.6)', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500 }}>
                    Legal basis
                  </strong>
                  <br />
                  {result.ico}
                </div>
                <div className="diag-result-pkg">{result.pkg}</div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link
                    to={`/packages?selected=${result.suggestedPackage}`}
                    className="btn-primary"
                    style={{ fontSize: '0.875rem', padding: '0.75rem 1.5rem', background: 'var(--paper)', color: 'var(--ink)' }}
                  >
                    See packages
                  </Link>
                  <button type="button" onClick={resetDiag} className="diag-btn diag-btn-back">Start over</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" id="faq">
        <div className="section-inner">
          <div className="section-header reveal" style={{ maxWidth: '620px' }}>
            <div className="section-label">FAQ</div>
            <h2 className="section-title">Frequently asked questions</h2>
            <p className="section-sub">Everything you need to know about DPO-as-a-Service and how WeDPO works.</p>
          </div>
          <div className="faq-grid reveal">
            {FAQS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div className="faq-item" key={f.q}>
                  <button
                    type="button"
                    className={`faq-trigger${open ? ' open' : ''}`}
                    onClick={() => setFaqOpen(open ? null : i)}
                  >
                    <span className="faq-q">{f.q}</span>
                    <span className="faq-icon">+</span>
                  </button>
                  <div className={`faq-body${open ? ' open' : ''}`}>
                    <p className="faq-a">{f.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PLATFORM */}
      <section className="section" id="platform">
        <div className="section-inner platform-inner">
          <div className="platform-hero reveal">
            <div>
              <div className="platform-eyebrow">Coming Soon</div>
              <div className="platform-title">
                The ultimate <em>AI-based operating system</em> for DPO functions and data governance
              </div>
              <p style={{ fontSize: '0.85rem', color: 'rgba(244,244,248,0.5)', fontStyle: 'italic', marginBottom: '1.25rem', fontWeight: 400 }}>
                Built by expert DPOs and data governance policy makers
              </p>
              <p className="platform-lead">
                Managing data protection compliance is complex, time-consuming and constantly changing. WeDPO brings to you a platform that brings together structured expert knowledge, operational tools, compliance automation and AI assistance, so your organisation stays compliant without the overhead.{' '}
                <strong style={{ color: 'var(--paper)', fontWeight: 500 }}>
                  Whether you're handling it in-house or working with a DPO, everything you need is in one place.
                </strong>
              </p>
              <div className="platform-stats">
                <div>
                  <div className="pstat-num">500K+</div>
                  <div className="pstat-label">Organisations required to appoint a DPO across EU/EEA</div>
                </div>
                <div>
                  <div className="pstat-num">15 yrs</div>
                  <div className="pstat-label">Of field experience behind the platform</div>
                </div>
                <div>
                  <div className="pstat-num">12</div>
                  <div className="pstat-label">Regulatory jurisdictions covered from day one</div>
                </div>
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <div className="pcard-back" />
              <div className="pcard">
                <div className="pcard-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Slash />
                    <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: '0.95rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                      we/dpo Platform
                    </span>
                  </div>
                  <div className="pcard-badge">Beta Preview</div>
                </div>
                <div className="pcard-metrics">
                  <div>
                    <div className="pcard-metric-val">12</div>
                    <div className="pcard-metric-lbl">Active clients</div>
                  </div>
                  <div>
                    <div className="pcard-metric-val">98%</div>
                    <div className="pcard-metric-lbl">Audit coverage</div>
                  </div>
                  <div>
                    <div className="pcard-metric-val">4.2h</div>
                    <div className="pcard-metric-lbl">Saved / week</div>
                  </div>
                </div>
                {[
                  ['ROPA completeness', 87],
                  ['Policy centre readiness', 72],
                  ['DSR SLA compliance', 100],
                ].map(([label, val]) => (
                  <div className="pcard-bar-row" key={label as string}>
                    <div className="pcard-bar-label"><span>{label}</span><span>{val}%</span></div>
                    <div className="pcard-bar"><div className="pcard-bar-fill" style={{ width: `${val}%` }} /></div>
                  </div>
                ))}
                <div className="pcard-ai">
                  <div className="pcard-ai-dot">✦</div>
                  <div className="pcard-ai-text">
                    <strong>AI DPO Assistant</strong>
                    "Does processing behavioural ad data for EU users require a DPIA?"{' '}
                    <span style={{ color: 'var(--g2)' }}>Generating expert answer...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pillars-grid">
            {PILLARS.map((p, i) => (
              <div key={p.num} className={`pillar-card reveal reveal-delay-${i + 1}`}>
                <div className="pillar-num">{p.num}</div>
                <div className="pillar-tag">{p.tag}</div>
                <div className="pillar-title">{p.title}</div>
                <p className="pillar-text">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="platform-waitlist reveal" id="waitlist">
            <h3>Be first to access the platform</h3>
            <p>The WeDPO platform is currently in early access with a select group of design partners. Join the waitlist and we will reach out when access opens to your organisation.</p>
            <WaitlistForm />
            <div className="waitlist-note">No spam. We'll only contact you about platform access.</div>
          </div>
        </div>
      </section>

      <CtaBand onProposal={() => openProposal()} />
      <FooterFull />

      {/* Modals */}
      {modal === 'proposal' && (
        <ProposalModal
          onClose={() => setModal(null)}
          initialPackage={selectedPackage}
          onClearPackage={() => setSelectedPackage('')}
        />
      )}
      {modal === 'ropa' && (
        <RopaModal onClose={() => setModal(null)} onProposal={() => openProposal()} />
      )}
    </div>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !org) return;
    setBusy(true);
    try {
      await submitWaitlist(email, org);
      setDone(true);
    } catch (err) {
      console.error('waitlist signup failed', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="waitlist-form" onSubmit={handleSubmit}>
      <input
        className="waitlist-input"
        type="email"
        required
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={done || busy}
      />
      <input
        className="waitlist-input"
        type="text"
        required
        placeholder="Organisation name"
        style={{ maxWidth: '200px', minWidth: '180px' }}
        value={org}
        onChange={(e) => setOrg(e.target.value)}
        disabled={done || busy}
      />
      <button
        type="submit"
        className="waitlist-btn"
        disabled={done || busy}
        style={done ? { background: 'var(--live)', color: '#0D2A22' } : undefined}
      >
        {done ? 'Joined ✓' : busy ? '…' : 'Join waitlist'}
      </button>
    </form>
  );
}
